import type { CreateOrderRequest } from "@shared/schemas";
import type { CreateOrderResponse, PublicOrder, PublicUser } from "@shared/types";

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const message = body && typeof body === "object" && "error" in body ? String((body as { error: unknown }).error) : res.statusText;
    throw new ApiError(res.status, message);
  }
  return body as T;
}

export const api = {
  health: () => request<{ ok: true }>("/api/health"),
  me: () => request<{ user: PublicUser | null }>("/api/auth/me"),
  requestMagicLink: (email: string) =>
    request<{ ok: true }>("/api/auth/magic-link", { method: "POST", body: JSON.stringify({ email }) }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  listOrders: () => request<{ orders: PublicOrder[] }>("/api/orders"),
  getOrder: (orderNumber: string) => request<{ order: PublicOrder }>(`/api/orders/${encodeURIComponent(orderNumber)}`),
  createOrder: (body: CreateOrderRequest) =>
    request<CreateOrderResponse>("/api/orders", { method: "POST", body: JSON.stringify(body) }),
};

export { ApiError };
